import React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'

export const WizardContext = createContext({})

export const WizardProvider = ({
  children,
  initialStep = 0,
  totalSteps = 1,
  disableNext = false,
  disablePrev = false
}) => {
  const [controlMode, setControlMode] = useState('single')
  const [currentStep, setCurrentStep] = useState(initialStep)
  const [isPrevDisabled, setIsPrevDisabled] = useState(disablePrev)
  const [isNextDisabled, setIsNextDisabled] = useState(disableNext)

  const canGoToNextStep = useMemo(
    () => currentStep + 1 < totalSteps,
    [totalSteps, currentStep]
  )

  const canGoToPrevStep = useMemo(() => currentStep - 1 >= 0, [currentStep])

  const setStep = useCallback(
    step => {
      const newStep = step instanceof Function ? step(currentStep) : step

      if (newStep >= 0 && newStep <= totalSteps) setCurrentStep(newStep)
    },
    [totalSteps, currentStep]
  )

  const goToNextStep = useCallback(() => {
    if (canGoToNextStep) setCurrentStep(step => step + 1)
  }, [canGoToNextStep])

  const goToPrevStep = useCallback(() => {
    if (canGoToPrevStep) setCurrentStep(step => step - 1)
  }, [canGoToPrevStep])

  const reset = useCallback(() => {
    setCurrentStep(initialStep)
  }, [initialStep])

  const togglePrev = useCallback(() => {
    setIsPrevDisabled(prev => !prev)
  }, [])

  const toggleNext = useCallback(() => {
    setIsNextDisabled(prev => !prev)
  }, [])

  return (
    <WizardContext.Provider
      value={{
        isSingleMode: controlMode === 'single',
        isMultipleMode: controlMode === 'multiple',
        isLastStep: controlMode === 'finish',
        controlMode,
        currentStep,
        totalSteps,
        isNextDisabled,
        isPrevDisabled,
        canGoToNextStep,
        canGoToPrevStep,
        goToNextStep,
        goToPrevStep,
        toggleNext,
        togglePrev,
        setIsNextDisabled,
        setIsPrevDisabled,
        setStep,
        setControlMode,
        reset
      }}
    >
      {children}
    </WizardContext.Provider>
  )
}

export const useWizard = () => {
  const context = useContext(WizardContext)

  if (context === null) {
    throw new Error('Attempted to call useWizard outside the Wizard component.')
  }

  return context
}
